import React, { useState, useEffect, useRef } from 'react';
import { Typography, Input, Button, Space, List, Avatar, Card, Modal } from 'antd';
import { SendOutlined, UserOutlined, RobotOutlined, ClearOutlined } from '@ant-design/icons';
import './chat.scss';
import Container from 'src/components/containers/Container';
import CommonContent from 'src/components/containers/CommonContent';
import { sendWebChatMessageWithHistory, ChatHistoryMessage } from 'src/services/gpt-service';
import { getSocket } from 'src/configs/socket-config';

const { TextArea } = Input;

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

const ChatPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hello! I am jAImee. How can I help you today?',
      sender: 'ai',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth < 768
  });
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef(getSocket());

  // Add window resize handler
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        isMobile: window.innerWidth < 768
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Setup socket connection
  useEffect(() => {
    const socket = socketRef.current;
    
    // Connect to the socket
    socket.connect();
    
    // Join the web chat room
    socket.joinWebChat((message) => {
      if (streamingMessageId) {
        setMessages(prevMessages => {
          return prevMessages.map(msg => {
            if (msg.id === streamingMessageId) {
              return { ...msg, text: message.content };
            }
            return msg;
          });
        });
      }
    });

    // Cleanup on unmount
    return () => {
      socket.leaveWebChat();
      socket.disconnect();
    };
  }, [streamingMessageId]);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const newUserMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages([...messages, newUserMessage]);
    setInputValue('');
    setIsLoading(true);

    // Create a placeholder for the AI response with streaming
    const streamingId = (Date.now() + 1).toString();
    const streamingMessage: Message = {
      id: streamingId,
      text: '',
      sender: 'ai',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, streamingMessage]);
    setStreamingMessageId(streamingId);

    try {
      // Convert the local message format to the API's expected format
      const messageHistory: ChatHistoryMessage[] = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));

      // Call API to get AI response - this will stream via websocket
      const response = await sendWebChatMessageWithHistory(inputValue, messageHistory);
      
      // Update streaming message ID to null once we get the complete response
      setStreamingMessageId(null);
    } catch (error) {
      console.error('Error sending message:', error);
      // Show fallback message on error
      setMessages(prev => {
        // Replace the streaming message with an error message
        return prev.map(msg => {
          if (msg.id === streamingId) {
            return {
              ...msg,
              text: 'I apologize, but I encountered an error. Please try again later.'
            };
          }
          return msg;
        });
      });
      setStreamingMessageId(null);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    Modal.confirm({
      title: 'Clear Chat',
      content: 'Are you sure you want to clear all messages? This cannot be undone.',
      okText: 'Clear',
      cancelText: 'Cancel',
      onOk: () => {
        setMessages([
          {
            id: Date.now().toString(),
            text: 'Hello! I am jAImee. How can I help you today?',
            sender: 'ai',
            timestamp: new Date(),
          }
        ]);
      }
    });
  };

  const headerActions = (
    <Button
      icon={<ClearOutlined />}
      onClick={clearChat}
    >
      {!screenSize.isMobile && 'Clear Chat'}
    </Button>
  );

  // Calculate height based on screen size
  const getCardHeight = () => {
    return screenSize.isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 150px)';
  };

  return (
    <Container className="ChatPage">
      <CommonContent 
        title="Chat with jAImee"
        action={headerActions}
      >
        <Card 
          className="chat-card"
          style={{ height: getCardHeight() }}
        >
          <div className="messages-container">
            <List
              itemLayout="horizontal"
              dataSource={messages}
              renderItem={(message) => (
                <List.Item className={`message-item ${message.sender}`}>
                  <List.Item.Meta
                    avatar={
                      message.sender === 'user' ? 
                        <Avatar icon={<UserOutlined />} /> : 
                        <Avatar icon={<RobotOutlined />} style={{ backgroundColor: '#1976d2' }} />
                    }
                    description={
                      <div className="message-content">
                        <div className="message-text">{message.text}</div>
                        <div className="message-time">
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
            <div ref={messagesEndRef} />
          </div>
          
          <div className="input-container">
            <Space.Compact style={{ width: '100%' }}>
              <TextArea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message here..."
                autoSize={{ minRows: 1, maxRows: 4 }}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <Button 
                type="primary" 
                icon={<SendOutlined />} 
                onClick={handleSendMessage}
                loading={isLoading}
              />
            </Space.Compact>
          </div>
        </Card>
      </CommonContent>
    </Container>
  );
};

export default ChatPage; 